import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  HelpCircle,
  Download,
  LogIn,
  UserPlus,
  Ship,
  Train,
  Truck,
  Plane,
  ShieldCheck,
  Landmark,
  Map,
  ArrowLeft,
} from 'lucide-react';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import logoMark from '../assets/logo_mark.svg';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRegistering, setIsRegistering] = useState(false);
  const [regType, setRegType] = useState('individual');

  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [authNotice, setAuthNotice] = useState('');

  const [formData, setFormData] = useState({
    fullName: '',
    fatherName: '',
    birthDate: null,
    nationalId: '',
    mobile: '',
    email: '',
    companyName: '',
    economicCode: '',
    registrationNumber: '',
    roles: [],
    username: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const msg = location?.state?.authMessage;
    if (msg) {
      setAuthNotice(String(msg));
      // Clear navigation state so the message doesn't persist.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUsers = () => JSON.parse(localStorage.getItem('corridor_users') || '[]');
  const saveUser = (user) => {
    const users = getUsers();
    users.push(user);
    localStorage.setItem('corridor_users', JSON.stringify(users));
  };

  const validateNationalId = (id) => {
    if (!/^\d{10}$/.test(id)) return false;
    const check = parseInt(id[9]);
    const sum = Array.from(id.slice(0, 9))
      .reduce((acc, curr, idx) => acc + parseInt(curr) * (10 - idx), 0) % 11;
    return sum < 2 ? check === sum : check === 11 - sum;
  };

  const validateMobile = (mobile) => /^09\d{9}$/.test(mobile);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError('');

    const users = getUsers();
    const user = users.find(u => u.username === loginData.username && u.password === loginData.password);

    if (user) {
      localStorage.setItem('current_user', JSON.stringify(user));
      navigate('/dashboard');
    } else {
      setLoginError('نام کاربری و یا رمز عبور شما اشتباه است');
    }
  };

  const handleRegister = (e) => {
    e.preventDefault();
    const newErrors = {};
    const users = getUsers();

    if (users.find(u => u.username === formData.username)) {
      newErrors.username = 'این نام کاربری قبلاً انتخاب شده است';
    }

    if (regType === 'individual') {
      if (!formData.fullName) newErrors.fullName = 'نام و نام خانوادگی الزامی است';
      if (!formData.fatherName) newErrors.fatherName = 'نام پدر الزامی است';
      if (!formData.birthDate) newErrors.birthDate = 'تاریخ تولد الزامی است';
      if (!validateNationalId(formData.nationalId)) newErrors.nationalId = 'کد ملی نامعتبر است';
    } else {
      if (!formData.companyName) newErrors.companyName = 'نام شرکت الزامی است';
      if (!/^\d{11}$/.test(formData.nationalId)) newErrors.nationalId = 'شناسه ملی باید ۱۱ رقم باشد';
      if (!formData.economicCode) newErrors.economicCode = 'کد اقتصادی الزامی است';
    }

    if (!validateMobile(formData.mobile)) newErrors.mobile = 'شماره موبایل نامعتبر است';
    if (!formData.username) newErrors.username = 'نام کاربری الزامی است';
    if (formData.password.length < 6) newErrors.password = 'رمز عبور باید حداقل ۶ کاراکتر باشد';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'تکرار رمز عبور مطابقت ندارد';

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const newUser = {
        username: formData.username,
        password: formData.password,
        fullName: regType === 'individual' ? formData.fullName : formData.companyName,
        type: regType,
        nationalId: formData.nationalId,
        mobile: formData.mobile,
        email: formData.email,
        roles: formData.roles,
        ...(regType === 'individual'
          ? {
              fatherName: formData.fatherName,
              birthDate: formData.birthDate?.format?.('YYYY/MM/DD') || ''
            }
          : {
              companyName: formData.companyName,
              economicCode: formData.economicCode,
              registrationNumber: formData.registrationNumber
            })
      };
      saveUser(newUser);
      alert('ثبت نام با موفقیت انجام شد. اکنون می‌توانید وارد شوید.');
      setIsRegistering(false);
      setLoginData({ username: newUser.username, password: newUser.password });
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-[IRANSansWeb,Tahoma] p-6 md:p-10" dir="rtl">
      <style>{`
        @keyframes corridorFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes corridorShake { 10%, 90% { transform: translateX(-1px); } 20%, 80% { transform: translateX(2px); } 30%, 50%, 70% { transform: translateX(-4px); } 40%, 60% { transform: translateX(4px); } }
        .animate-fade-in { animation: corridorFadeIn .35s ease-out both; }
        .animate-shake { animation: corridorShake .5s ease-in-out both; }
      `}</style>

      <div className="max-w-6xl mx-auto space-y-8">
        {/* HERO: brand/intro + sign-in card */}
        <header className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-6 md:p-10 overflow-hidden relative">
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,rgba(33,57,112,0.10),transparent_70%)]" />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Brand / intro side (right on desktop) */}
            <div className="text-justify order-2 lg:order-none">
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-2xl text-[11px] font-extrabold border border-blue-100">
                <ShieldCheck size={16} />
                <span>سامانه هوشمند کریدور جنوب-شمال</span>
              </div>

              <h1 className="mt-6 text-3xl md:text-4xl font-black text-[#1A3062] leading-tight">
                کریدور هوشمند شمال - جنوب
              </h1>

              <p className="mt-4 text-sm text-gray-500 leading-8 max-w-xl">
                درگاهی یکپارچه برای مدیریت لجستیک، حمل‌ونقل چندوجهی، تجارت فرامرزی و دسترسی به فرصت‌های سرمایه‌گذاری در
                مسیر راهبردی جنوب به شمال. برای استفاده از خدمات، وارد پنل کاربری شوید یا ثبت‌نام کنید.
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                <ModeChip icon={<Ship size={15} />} label="دریایی" />
                <ModeChip icon={<Train size={15} />} label="ریلی" />
                <ModeChip icon={<Truck size={15} />} label="جاده‌ای" />
                <ModeChip icon={<Plane size={15} />} label="هوایی (کارگو)" />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/faq"
                  className="h-12 px-5 rounded-2xl border border-blue-100 bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-sm transition inline-flex items-center justify-center gap-2"
                >
                  <HelpCircle size={18} />
                  سوالات متداول
                </Link>

                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  title="به‌زودی"
                  className="h-12 px-5 rounded-2xl border border-gray-200 bg-gray-50 text-gray-400 font-bold text-sm inline-flex items-center justify-center gap-2 cursor-not-allowed opacity-80"
                >
                  <Download size={18} />
                  دانلود اپلیکیشن
                  <span className="text-[10px] font-extrabold">(به‌زودی)</span>
                </button>
              </div>

              <div className="mt-8 hidden lg:flex items-center justify-center">
                <div className="bg-[#fbfdff] border border-gray-100 rounded-[2.5rem] p-5 shadow-sm">
                  <img
                    src="/logo.png"
                    alt="کریدور هوشمند شمال - جنوب"
                    className="w-40 h-40 object-contain select-none"
                    draggable={false}
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* Sign-in / register card (left on desktop) */}
            <div className="order-1 lg:order-none">
              <div className="bg-[#fbfdff] border border-gray-100 rounded-[2.5rem] p-6 md:p-8 shadow-sm">
                <div className="text-center mb-8">
                  <div className="flex justify-center mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 bg-[#213970] rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-900/20">
                        <img
                          src={logoMark}
                          alt="لوگوی کریدور هوشمند"
                          className="w-9 h-9 select-none"
                          draggable={false}
                        />
                      </div>
                      <div className="text-center flex flex-col items-center">
                        <h2 className="text-xl font-black text-[#1A3062]">کریدور هوشمند</h2>
                        <p className="text-[11px] text-blue-400 font-bold">شمال - جنوب</p>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-lg font-extrabold text-gray-800">
                    {isRegistering ? 'ثبت نام در سامانه' : 'ورود به پنل کاربری'}
                  </h3>
                  <p className="text-gray-400 text-xs mt-2">مدیریت هوشمند لجستیک و سرمایه‌گذاری</p>
                </div>

                {!isRegistering ? (
                  <form onSubmit={handleLogin} className="space-y-6">
                    {authNotice && (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-2xl text-xs flex items-center gap-2">
                        <AlertCircle size={16} /> {authNotice}
                      </div>
                    )}
                    {loginError && (
                      <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-2xl text-xs flex items-center gap-2 animate-shake">
                        <AlertTriangle size={16} /> {loginError}
                      </div>
                    )}
                    <div className="relative">
                      <label className="absolute -top-2.5 right-3 bg-[#fbfdff] px-2 text-xs text-gray-400">نام کاربری</label>
                      <input
                        dir="ltr"
                        type="text"
                        required
                        className="w-full h-14 px-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:border-blue-500 transition text-center font-bold"
                        value={loginData.username}
                        onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                      />
                    </div>

                    <div className="relative">
                      <label className="absolute -top-2.5 right-3 bg-[#fbfdff] px-2 text-xs text-gray-400">رمز عبور</label>
                      <input
                        dir="ltr"
                        type="password"
                        required
                        className="w-full h-14 px-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:border-blue-500 transition text-center font-bold"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full h-14 bg-[#213970] text-white font-bold rounded-2xl transition duration-300 shadow-lg shadow-blue-900/20 hover:bg-[#1A3062] inline-flex items-center justify-center gap-2"
                    >
                      <LogIn size={18} />
                      ورود به سامانه
                    </button>

                    <div className="text-center mt-6">
                      <button type="button" onClick={() => setIsRegistering(true)} className="text-blue-600 text-sm font-bold hover:underline inline-flex items-center gap-1">
                        <UserPlus size={16} />
                        هنوز عضو نشده‌اید؟ ثبت نام در سامانه
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleRegister} className="space-y-5">
                    {authNotice && (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-2xl text-xs flex items-center gap-2">
                        <AlertCircle size={16} /> {authNotice}
                      </div>
                    )}
                    <div className="flex bg-gray-100 p-1 rounded-2xl mb-6">
                      <button
                        type="button"
                        onClick={() => setRegType('individual')}
                        className={`flex-1 py-3 rounded-xl text-sm font-bold transition ${regType === 'individual' ? 'bg-white text-[#213970] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        شخص حقیقی
                      </button>
                      <button
                        type="button"
                        onClick={() => setRegType('legal')}
                        className={`flex-1 py-3 rounded-xl text-sm font-bold transition ${regType === 'legal' ? 'bg-white text-[#213970] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        شخص حقوقی
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {regType === 'individual' ? (
                        <>
                          <InputField label="نام و نام خانوادگی" value={formData.fullName} onChange={(v) => setFormData({ ...formData, fullName: v })} error={errors.fullName} />
                          <InputField label="نام پدر" value={formData.fatherName} onChange={(v) => setFormData({ ...formData, fatherName: v })} error={errors.fatherName} />
                          <InputField label="کد ملی" value={formData.nationalId} onChange={(v) => setFormData({ ...formData, nationalId: v })} error={errors.nationalId} dir="ltr" />
                          <JalaliDateField label="تاریخ تولد" value={formData.birthDate} onChange={(v) => setFormData({ ...formData, birthDate: v })} error={errors.birthDate} />
                        </>
                      ) : (
                        <>
                          <InputField label="نام شرکت" value={formData.companyName} onChange={(v) => setFormData({ ...formData, companyName: v })} error={errors.companyName} />
                          <InputField label="شناسه ملی شرکت" value={formData.nationalId} onChange={(v) => setFormData({ ...formData, nationalId: v })} error={errors.nationalId} dir="ltr" />
                          <InputField label="کد اقتصادی" value={formData.economicCode} onChange={(v) => setFormData({ ...formData, economicCode: v })} error={errors.economicCode} dir="ltr" />
                          <InputField label="شماره ثبت" value={formData.registrationNumber} onChange={(v) => setFormData({ ...formData, registrationNumber: v })} error={errors.registrationNumber} dir="ltr" />
                        </>
                      )}
                      <InputField label="شماره همراه" value={formData.mobile} onChange={(v) => setFormData({ ...formData, mobile: v })} error={errors.mobile} dir="ltr" placeholder="۰۹xxxxxxxxx" />
                      <InputField label="پست الکترونیک" value={formData.email} onChange={(v) => setFormData({ ...formData, email: v })} error={errors.email} dir="ltr" />
                      <InputField label="نام کاربری" value={formData.username} onChange={(v) => setFormData({ ...formData, username: v })} error={errors.username} dir="ltr" />
                      <RoleMultiSelect label="نقش" value={formData.roles} onChange={(roles) => setFormData({ ...formData, roles })} />
                      <InputField label="رمز عبور" type="password" value={formData.password} onChange={(v) => setFormData({ ...formData, password: v })} error={errors.password} dir="ltr" />
                      <InputField label="تکرار رمز عبور" type="password" value={formData.confirmPassword} onChange={(v) => setFormData({ ...formData, confirmPassword: v })} error={errors.confirmPassword} dir="ltr" />
                    </div>

                    <div className="pt-4 flex gap-4">
                      <button
                        type="button"
                        onClick={() => setIsRegistering(false)}
                        className="flex-1 h-14 border border-gray-200 bg-white text-gray-500 font-bold rounded-2xl hover:bg-gray-50 transition"
                      >
                        بازگشت
                      </button>
                      <button
                        type="submit"
                        className="flex-[2] h-14 bg-[#213970] text-white font-bold rounded-2xl shadow-lg shadow-blue-900/20 hover:bg-[#1A3062] transition"
                      >
                        تکمیل ثبت نام
                      </button>
                    </div>
                  </form>
                )}

                <div className="text-center mt-10 pt-6 border-t border-gray-100">
                  <p className="text-gray-400 text-[10px]">
                    تمامی حقوق برای شرکت{' '}
                    <span className="text-[11px] font-bold text-gray-500">پیمان متین اطلس</span>{' '}
                    محفوظ است.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Benefits */}
        <section className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm p-8">
          <div className="flex items-start justify-between gap-6">
            <div className="text-justify">
              <h2 className="text-lg font-black text-gray-800">مزایای عضویت در سامانه</h2>
              <p className="text-[11px] text-gray-400 mt-2 leading-6">
                با ورود به پنل کاربری، به مجموعه‌ای از خدمات یکپارچه کریدور دسترسی خواهید داشت.
              </p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700">
              <ShieldCheck size={22} />
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Benefit
              icon={<ShieldCheck size={18} className="text-blue-700" />}
              title="تعرفه‌های ترجیحی حمل‌ونقل"
              desc="دسترسی به نرخ‌ها و تعرفه‌های ترجیحی در مسیرهای منتخب کریدور جنوب به شمال."
            />
            <Benefit
              icon={<Landmark size={18} className="text-blue-700" />}
              title="مشارکت در سرمایه‌گذاری"
              desc="مشاهده پروژه‌ها، مدل‌های مشارکت و اعلام علاقه‌مندی برای فرصت‌های سرمایه‌گذاری."
            />
            <Benefit
              icon={<Map size={18} className="text-blue-700" />}
              title="رهگیری هوشمند محموله"
              desc="رهگیری یکپارچه و لحظه‌ای محموله‌ها در حمل دریایی، ریلی، جاده‌ای و هوایی."
            />
          </div>
        </section>

        {/* FAQ CTA band */}
        <div className="bg-[#1A3062] rounded-[2.5rem] p-8 md:p-10 text-white shadow-xl overflow-hidden relative">
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.18),transparent_70%)]" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-black">سوالی درباره سامانه دارید؟</h3>
              <p className="text-[11px] text-blue-200 mt-3 leading-6">
                پاسخ پرتکرارترین پرسش‌ها درباره ثبت‌نام، رهگیری محموله، تعرفه‌ها و خدمات کریدور را در صفحه سوالات متداول
                ببینید.
              </p>
            </div>
            <Link
              to="/faq"
              className="h-12 px-6 rounded-2xl bg-yellow-400 hover:bg-yellow-500 text-[#1A3062] font-black transition inline-flex items-center gap-2 shadow-lg whitespace-nowrap"
            >
              <HelpCircle size={18} />
              مشاهده سوالات متداول
              <ArrowLeft size={18} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModeChip = ({ icon, label }) => (
  <span className="inline-flex items-center gap-2 bg-[#fbfdff] border border-gray-100 text-gray-600 px-3 py-2 rounded-2xl text-[11px] font-bold">
    <span className="text-blue-700">{icon}</span>
    {label}
  </span>
);

const Benefit = ({ icon, title, desc }) => (
  <div className="rounded-2xl border border-gray-100 bg-[#fbfdff] p-5 hover:shadow-sm transition">
    <div className="flex items-center gap-2">
      <div className="w-10 h-10 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center">
        {icon}
      </div>
      <div className="text-sm font-extrabold text-gray-800">{title}</div>
    </div>
    <div className="text-[11px] text-gray-400 mt-2 leading-7">{desc}</div>
  </div>
);

const InputField = ({ label, value, onChange, error, dir = "rtl", type = "text", placeholder = "", wrapperClassName = "" }) => (
  <div className={`space-y-1 ${wrapperClassName}`.trim()}>
    <div className="relative">
      <label className="absolute -top-2.5 right-3 bg-[#fbfdff] px-2 text-[10px] text-gray-400 z-10">{label}</label>
      <input
        type={type}
        dir={dir}
        placeholder={placeholder}
        className={`w-full h-12 px-4 bg-white border rounded-xl focus:outline-none focus:border-blue-500 transition text-sm font-medium ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
    {error && <p className="text-[10px] text-red-500 pr-2 flex items-center gap-1"><AlertTriangle size={10} /> {error}</p>}
  </div>
);

const JalaliDateField = ({ label, value, onChange, error, wrapperClassName = "" }) => (
  <div className={`space-y-1 ${wrapperClassName}`.trim()}>
    <div className="relative">
      <label className="absolute -top-2.5 right-3 bg-[#fbfdff] px-2 text-[10px] text-gray-400 z-10">{label}</label>
      <DatePicker
        value={value}
        onChange={onChange}
        calendar={persian}
        locale={persian_fa}
        format="YYYY/MM/DD"
        calendarPosition="bottom-right"
        placeholder="۱۳۷۰/۰۶/۲۵"
        inputClass={`w-full h-12 px-4 bg-white border rounded-xl focus:outline-none focus:border-blue-500 transition text-sm font-medium ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
        containerClassName="w-full"
      />
    </div>
    {error && <p className="text-[10px] text-red-500 pr-2 flex items-center gap-1"><AlertTriangle size={10} /> {error}</p>}
  </div>
);

const ROLE_OPTIONS = ['بازرگان', 'سرمایه گذار', 'حمل و نقل'];

const RoleMultiSelect = ({ label, value, onChange, error, wrapperClassName = "" }) => (
  <div className={`space-y-1 ${wrapperClassName}`.trim()}>
    <div className="relative">
      <label className="absolute -top-2.5 right-3 bg-[#fbfdff] px-2 text-[10px] text-gray-400 z-10">{label}</label>
      <div className={`w-full min-h-12 px-3 py-2 border rounded-xl bg-white flex flex-wrap gap-2 items-center ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
        {ROLE_OPTIONS.map((role) => {
          const active = (value || []).includes(role);
          return (
            <button
              key={role}
              type="button"
              onClick={() => {
                const next = active ? (value || []).filter(r => r !== role) : [...(value || []), role];
                onChange(next);
              }}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition ${
                active
                  ? 'bg-[#213970] text-white border-[#213970]'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {role}
            </button>
          );
        })}
      </div>
    </div>
    {error && <p className="text-[10px] text-red-500 pr-2 flex items-center gap-1"><AlertTriangle size={10} /> {error}</p>}
  </div>
);

export default Login;
